import ViralLoopsFormWidget from "@/shared/components/ViralLoopsFormWidget/ViralLoopsFormWidget";
import React, { lazy, Suspense, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import { Spinner } from "@/shared/components";
import InvitationStatuses from "./InvitationStatuses/InvitationStatuses";
import ExpandableCard from "@/shared/components/ExpandableCard/ExpandableCard";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { getReferalBonuses, sortBonuses } from "@/cms/components/Rewards/misc/Api";
import { getUserId, isAuthenticated } from "@/api/localStorage";
import { updateUserOnboarding } from "@/api/referral";
import Reward from "@/cms/components/Rewards/components/Reward";
import downloadIcon from "./assets/download.png";
import DesktopHeader from "./assets/desktopHeader.png";
import MobileHeader from "./assets/mobileHeader.png";
import { useIsMobile } from "@/cms/useIsMobile";

const LazyApolloWrapper = lazy(() => import("@/App/components/ApolloWrapper"));
const LazyFaqWedget = lazy(() => import("./FaqWedget/FaqWedget"));

function ReferFriend() {
  const { t } = useTranslation();
  const ucid = "c9ytKvFCAdxoXDBfUrKwm1QjwaE";
  const popup = false;
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();
  const userId = getUserId();
  const isUserAuthenticated = isAuthenticated();
  const queryClient = useQueryClient();
  const onboardingCalled = useRef(false);

  const { mutate: updateOnboarding } = useMutation(() => updateUserOnboarding(userId), {
    onError: (error) => {
      console.error("Error updating user onboarding:", error);
    },
  });

  const { data: referalRewards, isLoading: referalRewardsLoading } = useQuery(
    "referal-bonuses",
    () => getReferalBonuses(userId),
    {
      enabled: !!userId && isUserAuthenticated,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    const mainRoot = document.querySelector("#main-root");
    if (mainRoot) {
      mainRoot.classList.add("whitePage");
    }

    // Add timeout to show loading state 2000
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    // Update user onboarding when component mounts, but only once
    if (userId && isUserAuthenticated && !onboardingCalled.current) {
      onboardingCalled.current = true;
      updateOnboarding();
    }

    return () => {
      clearTimeout(timer);
      const mainRoot = document.querySelector("#main-root");
      if (mainRoot) {
        mainRoot.classList.remove("whitePage");
      }
    };
  }, [userId, isUserAuthenticated, updateOnboarding]);

  if (isLoading || referalRewardsLoading) {
    return (
      <div className="spinner-container">
        <Spinner />
      </div>
    );
  }

  const reLoadReferalRewards = () => {
    queryClient.invalidateQueries("referal-bonuses");
  };

  const renderReferralRewards = () => {
    if (!referalRewards || referalRewards.length === 0) {
      return null;
    }

    const { requestedOpen, paid, waiting, rejected } = sortBonuses(
      (referalRewards || []).map((bonus) => {
        return { ...bonus, transactionId: bonus.referralRelationId ?? "" };
      })
    );
    if (waiting.length === 0 && requestedOpen.length === 0 && paid.length === 0 && rejected.length === 0) {
      return null;
    }
    return (
      <div className="referral-rewards">
        <div className="referral-rewards-title">
          <h2>Übersicht - Meine Gutscheine</h2>
          <p>
            Ihre Gutscheine werden direkt auf dieser Übersichtsseite angezeigt. Sie erhalten einen Gutschein-Code, den
            Sie auf bestchoice.einloesen.de einlösen können. Dort stehen Ihnen über 500 attraktive Partner zur Auswahl,
            darunter auch Amazon.
          </p>
        </div>
        {waiting.length > 0 && (
          <div className="rewards-section">
            <h3>{t("rewards.headers.ready")}</h3>
            {waiting.map((bonus, index) => (
              <Reward
                key={bonus.bonusStatus + index}
                type="waiting"
                content={bonus}
                updateContent={reLoadReferalRewards}
              />
            ))}
          </div>
        )}
        {requestedOpen.length > 0 && (
          <div className="rewards-section">
            <h3>{t("rewards.headers.processing")}</h3>
            {requestedOpen.map((bonus, index) => (
              <Reward
                key={bonus.bonusStatus + index}
                type="open"
                content={bonus}
                updateContent={reLoadReferalRewards}
              />
            ))}
          </div>
        )}
        {paid.length > 0 && (
          <div className="rewards-section">
            <h3>{t("rewards.headers.paid")}</h3>
            {paid.map((bonus, index) => (
              <Reward
                key={bonus.bonusStatus + index}
                type="paid"
                content={bonus}
                updateContent={reLoadReferalRewards}
              />
            ))}
          </div>
        )}
        {rejected.length > 0 && (
          <div className="rewards-section">
            <h3>{t("rewards.headers.rejected")}</h3>
            {rejected.map((bonus, index) => (
              <Reward
                key={bonus.bonusStatus + index}
                type="rejected"
                content={bonus}
                updateContent={reLoadReferalRewards}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="refer-friend-page">
      <div className="header-top">
        {isMobile ? (
          <img className="header-img" src={MobileHeader} alt="mobile-header" />
        ) : (
          <img className="header-img" src={DesktopHeader} alt="desktop-header" />
        )}
      </div>
      <div className="refer-friend-page-content">
        <div className="pageTitle-container">
          <div className="pageTitle">
            Lassen Sie Ihre Freunde & Familie ebenfalls von remind.me profitieren – Es zahlt sich aus!
          </div>
          <div className="pageTitle-subTitle">
            Empfehlen Sie unseren kostenlosen Wechselservice an Ihre Freunde und Familie weiter – und sichern Sie sich
            und Ihren Liebsten jeweils einen 25 € BestChoice-Premium-Gutschein als Dankeschön!
          </div>
        </div>
        <div className="dashboardWidgedCards">
          <div className="dashboardWidgedCard">
            <ViralLoopsFormWidget ucid={ucid} popup={popup} tag="form-widget" />
            <div className="instructions">
              <h2 className="instructions-title">{t("referFriend.howItWorks")}</h2>
              <div className="instructions-steps">
                <ExpandableCard
                  title="1. Teilen Sie Ihren Einladungslink mit Ihren Freunden und Familie"
                  description="Melden sich Ihre Freunde über Ihren persönlichen Einladungslink bei remind.me an, können Sie den Status Ihrer Einladungen auf dieser Seite im Dashboard verfolgen."
                  value={undefined}
                  isDisabled={false}
                />
                <ExpandableCard
                  title="2. Unsere Experten führen den Wechsel für Ihren Freund durch"
                  description="Sobald der Wechsel Ihres Freundes erfolgreich abgeschlossen und bestätigt ist, informieren wir Sie per E-Mail. Ihren Gutschein können Sie dann in Ihrem Kundenkonto anfordern."
                  value={undefined}
                  isDisabled={false}
                />
                <ExpandableCard
                  title='3. Gutschein-Ansprüche entnehmen Sie bitte Ihrer "Übersicht - Meine Gutscheine"'
                  description="Die Durchführung und Bestätigung des Wechsels Ihres eingeladenen Freundes kann aufgrund von Vertrags- und Bearbeitungszeiten des Versorgers bis zu sechs Monate dauern. Die Auszahlung des Gutscheins erfolgt, sobald der Wechsel erfolgreich abgeschlossen und vom Versorger bestätigt wurde."
                  value={undefined}
                  isDisabled={false}
                />
              </div>
            </div>
          </div>
          <div className="dashboardWidgedCard">
            <InvitationStatuses />
          </div>
        </div>

        <div className="pageFooter">
          <div className="download-terms">
            <a
              href="https://www.remind.me/internal/freundschaftswerbung-teilnahmebedingungen"
              className="download-terms-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                window.open("https://www.remind.me/internal/freundschaftswerbung-teilnahmebedingungen", "_blank");
              }}
            >
              Erweiterte Teilnahmebedingungen
              <img className="download-icon" src={downloadIcon} alt="download" />
            </a>
          </div>
          {t("referFriend.termsAgreement")} {t("referFriend.termsLink")} {t("referFriend.termsAgreementEnd")}
        </div>
      </div>
      {renderReferralRewards()}
      <div className="referral-rewards referral-faq-section">
        <div className="faqWidgetCard">
          <LazyApolloWrapper
            component={
              <Suspense fallback={""}>
                <LazyFaqWedget />
              </Suspense>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default ReferFriend;
